import type { FindingResponse } from '../../../types';
import { isFlagForReview } from '../../findings/isFlagForReview';

/**
 * The catalog's field/catalog-scoped findings, indexed for the Attributes tab
 * (SC-2519/SC-2524). `field`-scoped findings key by each name in `finding.fields`
 * — the field name is a direct join to the attribute rows; `catalog`-scoped
 * findings are field-less and surface in the catalog-recommendations card.
 */
export interface AttributeFindings {
  /** Field name → its `field`-scoped findings (in catalog order). */
  byField: Map<string, FindingResponse[]>;
  /** Field-less `catalog`-scoped findings (vertical / locked fields / reviews). */
  catalog: FindingResponse[];
}

const EMPTY: readonly FindingResponse[] = [];

/**
 * Split a flat findings list (already filtered to pending/acknowledged + the
 * `field`/`catalog` scopes by the query) into the per-field index and the
 * field-less catalog list. A `field`-scoped finding touches one or more `fields`
 * and is indexed under each; one with an empty `fields` (off-contract) is dropped
 * from the per-field index rather than crashing the row.
 */
export function buildAttributeFindings(findings: readonly FindingResponse[]): AttributeFindings {
  const byField = new Map<string, FindingResponse[]>();
  const catalog: FindingResponse[] = [];
  for (const f of findings) {
    if (f.scope === 'field') {
      for (const field of f.fields) {
        const list = byField.get(field);
        if (list) list.push(f);
        else byField.set(field, [f]);
      }
    } else if (f.scope === 'catalog') {
      catalog.push(f);
    }
  }
  return { byField, catalog };
}

/** The findings for one field, or an empty list — never undefined (stable read). */
export function findingsForField(
  index: AttributeFindings,
  field: string,
): readonly FindingResponse[] {
  return index.byField.get(field) ?? EMPTY;
}

/**
 * The suggestion (✦) vs review (⚑) split for the flag pills. A `flag_for_review`
 * finding is a review; everything else (split/rename/…) is an actionable suggestion.
 */
export interface FindingTally {
  fix: number;
  review: number;
}

export function findingTally(findings: readonly FindingResponse[]): FindingTally {
  let fix = 0;
  let review = 0;
  for (const f of findings) {
    if (isFlagForReview(f)) review += 1;
    else fix += 1;
  }
  return { fix, review };
}
